import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Check, Image as ImageIcon, Loader2, Plus, Trash2, Settings, Upload, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../api/client';

// Curated image library organized by category - 18 images per category (108 total)
const DEFAULT_IMAGE_LIBRARY = {
  'ai-courses': {
    label: 'AI & Technology',
    icon: '🤖',
    images: [
      // Original 6
      { url: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=225&fit=crop', alt: 'Futuristic AI 3D Render' },
      { url: 'https://images.unsplash.com/photo-1717501218198-816a64915f81?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1717501218198-816a64915f81?w=400&h=225&fit=crop', alt: 'AI Biology & Protein Folding' },
      { url: 'https://images.unsplash.com/photo-1717501219263-9aa2d6a768d0?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1717501219263-9aa2d6a768d0?w=400&h=225&fit=crop', alt: 'AI Sustainability Illustration' },
      { url: 'https://images.unsplash.com/photo-1717501218504-81ed5eb52cd0?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1717501218504-81ed5eb52cd0?w=400&h=225&fit=crop', alt: 'AI Problem Solving' },
      { url: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=400&h=225&fit=crop', alt: 'Robot Head AI' },
      { url: 'https://images.unsplash.com/photo-1555255707-c07966088b7b?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1555255707-c07966088b7b?w=400&h=225&fit=crop', alt: 'Neural Network Visualization' },
      // New 12
      { url: 'https://images.unsplash.com/photo-1760629863094-5b1e8d1aae74?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1760629863094-5b1e8d1aae74?w=400&h=225&fit=crop', alt: 'Humanoid Robot Future' },
      { url: 'https://images.unsplash.com/photo-1768323102304-a06c1cdec8b8?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1768323102304-a06c1cdec8b8?w=400&h=225&fit=crop', alt: 'AI Robot Character' },
      { url: 'https://images.unsplash.com/photo-1768400730810-5c4398d58ae7?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1768400730810-5c4398d58ae7?w=400&h=225&fit=crop', alt: 'Orange Robot Assistant' },
      { url: 'https://images.pexels.com/photos/8566470/pexels-photo-8566470.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/8566470/pexels-photo-8566470.jpeg?w=400&h=225&fit=crop', alt: 'AI Technology Blue' },
      { url: 'https://images.pexels.com/photos/8566469/pexels-photo-8566469.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/8566469/pexels-photo-8566469.jpeg?w=400&h=225&fit=crop', alt: 'Futuristic Robot' },
      { url: 'https://images.unsplash.com/photo-1695902173528-0b15104c4554?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1695902173528-0b15104c4554?w=400&h=225&fit=crop', alt: 'AI Face Recognition' },
      { url: 'https://images.unsplash.com/photo-1764549906172-0153db0825bc?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1764549906172-0153db0825bc?w=400&h=225&fit=crop', alt: 'Digital Human Vision' },
      { url: 'https://images.unsplash.com/photo-1717583191083-cd82ed7f217e?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1717583191083-cd82ed7f217e?w=400&h=225&fit=crop', alt: 'AI Head Digital Art' },
      { url: 'https://images.unsplash.com/photo-1674027215016-0a4abfdbf1cc?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1674027215016-0a4abfdbf1cc?w=400&h=225&fit=crop', alt: 'Digital Brain Concept' },
      { url: 'https://images.pexels.com/photos/30855416/pexels-photo-30855416.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/30855416/pexels-photo-30855416.jpeg?w=400&h=225&fit=crop', alt: 'AI Scrabble Letters' },
      { url: 'https://images.pexels.com/photos/30869149/pexels-photo-30869149.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/30869149/pexels-photo-30869149.jpeg?w=400&h=225&fit=crop', alt: 'AI News Technology' },
    ]
  },
  'retail': {
    label: 'Retail & E-commerce',
    icon: '🛒',
    images: [
      // Original 6
      { url: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=225&fit=crop', alt: 'Modern Retail Store' },
      { url: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=225&fit=crop', alt: 'Shopping Cart Analytics' },
      { url: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=400&h=225&fit=crop', alt: 'Shopping Mall Interior' },
      { url: 'https://images.unsplash.com/photo-1534452203293-494d7ddbf7e0?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1534452203293-494d7ddbf7e0?w=400&h=225&fit=crop', alt: 'Retail Store Shelves' },
      { url: 'https://images.unsplash.com/photo-1556742111-a301076d9d18?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1556742111-a301076d9d18?w=400&h=225&fit=crop', alt: 'E-commerce Dashboard' },
      { url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop', alt: 'Analytics Dashboard' },
      // New 12
      { url: 'https://images.unsplash.com/photo-1605513524042-426bace35fc9?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1605513524042-426bace35fc9?w=400&h=225&fit=crop', alt: 'Order & Collect Sign' },
      { url: 'https://images.unsplash.com/photo-1605513524006-063ed6ed31e7?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1605513524006-063ed6ed31e7?w=400&h=225&fit=crop', alt: 'Digital Storefront' },
      { url: 'https://images.unsplash.com/photo-1688561807403-ba262590f86f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1688561807403-ba262590f86f?w=400&h=225&fit=crop', alt: 'Online Store Management' },
      { url: 'https://images.pexels.com/photos/5264895/pexels-photo-5264895.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/5264895/pexels-photo-5264895.jpeg?w=400&h=225&fit=crop', alt: 'Fashion Retail Store' },
      { url: 'https://images.pexels.com/photos/8201206/pexels-photo-8201206.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/8201206/pexels-photo-8201206.jpeg?w=400&h=225&fit=crop', alt: 'Happy Shopper' },
      { url: 'https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?w=400&h=225&fit=crop', alt: 'Warehouse Forklift' },
      { url: 'https://images.unsplash.com/photo-1740914994162-0b2a49280aeb?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1740914994162-0b2a49280aeb?w=400&h=225&fit=crop', alt: 'Warehouse Worker Inventory' },
      { url: 'https://images.unsplash.com/photo-1768796373307-fc2f843660f8?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1768796373307-fc2f843660f8?w=400&h=225&fit=crop', alt: 'Distribution Center' },
      { url: 'https://images.unsplash.com/photo-1721937127582-ed331de95a04?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1721937127582-ed331de95a04?w=400&h=225&fit=crop', alt: 'Warehouse Shelves' },
      { url: 'https://images.pexels.com/photos/7018659/pexels-photo-7018659.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/7018659/pexels-photo-7018659.jpeg?w=400&h=225&fit=crop', alt: 'Inventory Management' },
      { url: 'https://images.pexels.com/photos/30824313/pexels-photo-30824313.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/30824313/pexels-photo-30824313.jpeg?w=400&h=225&fit=crop', alt: 'Supply Chain Operations' },
    ]
  },
  'financial': {
    label: 'Financial Services',
    icon: '💰',
    images: [
      // Original 6
      { url: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=225&fit=crop', alt: 'Stock Market Charts' },
      { url: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=400&h=225&fit=crop', alt: 'Bitcoin & Cryptocurrency' },
      { url: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=225&fit=crop', alt: 'Financial Analysis' },
      { url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop', alt: 'Data Analytics Dashboard' },
      { url: 'https://images.unsplash.com/photo-1642790551116-18e150f248e5?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1642790551116-18e150f248e5?w=400&h=225&fit=crop', alt: 'Trading Platform' },
      { url: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1618044733300-9472054094ee?w=400&h=225&fit=crop', alt: 'Stock Market News' },
      // New 12
      { url: 'https://images.unsplash.com/photo-1766218329569-53c9270bb305?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1766218329569-53c9270bb305?w=400&h=225&fit=crop', alt: 'Trading Desk Setup' },
      { url: 'https://images.unsplash.com/photo-1766218326892-4b261b02a03f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1766218326892-4b261b02a03f?w=400&h=225&fit=crop', alt: 'Stock Charts Analysis' },
      { url: 'https://images.unsplash.com/photo-1761850167081-473019536383?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1761850167081-473019536383?w=400&h=225&fit=crop', alt: 'Mobile Trading App' },
      { url: 'https://images.unsplash.com/photo-1767424196045-030bbde122a4?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1767424196045-030bbde122a4?w=400&h=225&fit=crop', alt: 'Candlestick Chart Tablet' },
      { url: 'https://images.pexels.com/photos/35118250/pexels-photo-35118250.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/35118250/pexels-photo-35118250.jpeg?w=400&h=225&fit=crop', alt: 'Stock Trading Screen' },
      { url: 'https://images.pexels.com/photos/35118242/pexels-photo-35118242.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/35118242/pexels-photo-35118242.jpeg?w=400&h=225&fit=crop', alt: 'Market Analysis' },
      { url: 'https://images.unsplash.com/photo-1748439435495-722cc1728b7e?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1748439435495-722cc1728b7e?w=400&h=225&fit=crop', alt: 'Blockchain Trading Desk' },
      { url: 'https://images.unsplash.com/photo-1736751035793-353baaa416cf?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1736751035793-353baaa416cf?w=400&h=225&fit=crop', alt: 'Crypto Analytics' },
      { url: 'https://images.unsplash.com/photo-1768242079046-c9c633187db1?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1768242079046-c9c633187db1?w=400&h=225&fit=crop', alt: 'Cryptocurrency Interface' },
      { url: 'https://images.unsplash.com/photo-1516245834210-c4c142787335?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1516245834210-c4c142787335?w=400&h=225&fit=crop', alt: 'Bitcoin Trading' },
      { url: 'https://images.pexels.com/photos/6132751/pexels-photo-6132751.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/6132751/pexels-photo-6132751.jpeg?w=400&h=225&fit=crop', alt: 'Crypto ATM Machine' },
      { url: 'https://images.pexels.com/photos/19825344/pexels-photo-19825344.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/19825344/pexels-photo-19825344.jpeg?w=400&h=225&fit=crop', alt: 'Fintech Letters' },
    ]
  },
  'life-sciences': {
    label: 'Life Sciences & Healthcare',
    icon: '🧬',
    images: [
      // Original 6
      { url: 'https://images.unsplash.com/photo-1581093458791-9f3c3250a8b0?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1581093458791-9f3c3250a8b0?w=400&h=225&fit=crop', alt: 'Laboratory Research' },
      { url: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=225&fit=crop', alt: 'DNA Molecular Structure' },
      { url: 'https://images.unsplash.com/photo-1579165466949-3180a3d056d5?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1579165466949-3180a3d056d5?w=400&h=225&fit=crop', alt: 'Medical Laboratory' },
      { url: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400&h=225&fit=crop', alt: 'Pharmaceutical Research' },
      { url: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?w=400&h=225&fit=crop', alt: 'Medical Science' },
      { url: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=225&fit=crop', alt: 'Healthcare Data' },
      // New 12
      { url: 'https://images.unsplash.com/photo-1760074032551-fef8992ca72d?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1760074032551-fef8992ca72d?w=400&h=225&fit=crop', alt: 'Blood Sample Analysis' },
      { url: 'https://images.unsplash.com/photo-1582560486381-e6a01d8f5bb7?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1582560486381-e6a01d8f5bb7?w=400&h=225&fit=crop', alt: 'Lab Filtration Process' },
      { url: 'https://images.unsplash.com/photo-1576669801838-1b1c52121e6a?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1576669801838-1b1c52121e6a?w=400&h=225&fit=crop', alt: 'Clinical Laboratory Equipment' },
      { url: 'https://images.unsplash.com/photo-1768498950658-87ecfe232b59?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1768498950658-87ecfe232b59?w=400&h=225&fit=crop', alt: 'Medical Professionals Lab' },
      { url: 'https://images.pexels.com/photos/6627667/pexels-photo-6627667.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/6627667/pexels-photo-6627667.jpeg?w=400&h=225&fit=crop', alt: 'Lab Centrifuge Operation' },
      { url: 'https://images.pexels.com/photos/9629677/pexels-photo-9629677.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/9629677/pexels-photo-9629677.jpeg?w=400&h=225&fit=crop', alt: 'Healthcare Professional' },
      { url: 'https://images.unsplash.com/photo-1748263582756-082fe4adaa5b?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1748263582756-082fe4adaa5b?w=400&h=225&fit=crop', alt: 'Eppendorf Tubes' },
      { url: 'https://images.unsplash.com/photo-1748280933446-c9be297ec3be?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1748280933446-c9be297ec3be?w=400&h=225&fit=crop', alt: 'Hemolysis Test Tubes' },
      { url: 'https://images.unsplash.com/photo-1748278739348-d9886621530f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1748278739348-d9886621530f?w=400&h=225&fit=crop', alt: 'Lab Centrifuge' },
      { url: 'https://images.unsplash.com/photo-1748263831502-23d923a5c66a?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1748263831502-23d923a5c66a?w=400&h=225&fit=crop', alt: 'Lab Scale Measurement' },
      { url: 'https://images.pexels.com/photos/8851791/pexels-photo-8851791.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/8851791/pexels-photo-8851791.jpeg?w=400&h=225&fit=crop', alt: 'Scientist Lab Work' },
      { url: 'https://images.pexels.com/photos/5922106/pexels-photo-5922106.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/5922106/pexels-photo-5922106.jpeg?w=400&h=225&fit=crop', alt: 'Pharmaceutical Vials' },
    ]
  },
  'machine-learning': {
    label: 'Machine Learning & Data',
    icon: '🧠',
    images: [
      // Original 6
      { url: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=400&h=225&fit=crop', alt: 'Code on Screen' },
      { url: 'https://images.unsplash.com/photo-1518932945647-7a1c969f8be2?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1518932945647-7a1c969f8be2?w=400&h=225&fit=crop', alt: 'Data Visualization' },
      { url: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=225&fit=crop', alt: 'Analytics Charts' },
      { url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop', alt: 'Data Dashboard' },
      { url: 'https://images.unsplash.com/photo-1488229297570-58520851e868?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1488229297570-58520851e868?w=400&h=225&fit=crop', alt: 'Digital Network' },
      { url: 'https://images.unsplash.com/photo-1509228627152-72ae9ae6848d?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1509228627152-72ae9ae6848d?w=400&h=225&fit=crop', alt: 'Machine Learning Concept' },
      // New 12
      { url: 'https://images.unsplash.com/photo-1717501219345-06ea2bf3eb80?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1717501219345-06ea2bf3eb80?w=400&h=225&fit=crop', alt: 'Quantum Computing AI' },
      { url: 'https://images.unsplash.com/photo-1717501218661-0322e4bc4c81?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1717501218661-0322e4bc4c81?w=400&h=225&fit=crop', alt: 'Generative AI Art' },
      { url: 'https://images.pexels.com/photos/17484975/pexels-photo-17484975.png?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/17484975/pexels-photo-17484975.png?w=400&h=225&fit=crop', alt: 'Neural Network Colorful' },
      { url: 'https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/17485657/pexels-photo-17485657.png?w=400&h=225&fit=crop', alt: 'Data Science Visualization' },
      { url: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1763038311036-6d18805537e5?w=400&h=225&fit=crop', alt: 'Business Analyst Dashboard' },
      { url: 'https://images.unsplash.com/photo-1758876202980-0a28b744fb24?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758876202980-0a28b744fb24?w=400&h=225&fit=crop', alt: 'Data Discussion Meeting' },
      { url: 'https://images.unsplash.com/photo-1686061593213-98dad7c599b9?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1686061593213-98dad7c599b9?w=400&h=225&fit=crop', alt: 'Analytics Dashboard Dark' },
      { url: 'https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?w=400&h=225&fit=crop', alt: 'Line Chart Analysis' },
      { url: 'https://images.pexels.com/photos/7013070/pexels-photo-7013070.png?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/7013070/pexels-photo-7013070.png?w=400&h=225&fit=crop', alt: 'Data Working Laptop' },
    ]
  },
  'business': {
    label: 'Business & Strategy',
    icon: '📊',
    images: [
      // Original 6
      { url: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=225&fit=crop', alt: 'Business Meeting' },
      { url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=225&fit=crop', alt: 'Team Collaboration' },
      { url: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=225&fit=crop', alt: 'Business Presentation' },
      { url: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=400&h=225&fit=crop', alt: 'Strategy Planning' },
      { url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop', alt: 'Analytics Overview' },
      { url: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=225&fit=crop', alt: 'Team Working' },
      // New 12
      { url: 'https://images.unsplash.com/photo-1760346546771-a81d986459ff?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1760346546771-a81d986459ff?w=400&h=225&fit=crop', alt: 'Corporate Meeting Room' },
      { url: 'https://images.unsplash.com/photo-1758518727343-e578b7795ad5?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758518727343-e578b7795ad5?w=400&h=225&fit=crop', alt: 'Business Discussion Sofa' },
      { url: 'https://images.unsplash.com/photo-1758518725921-1eb74ed293be?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758518725921-1eb74ed293be?w=400&h=225&fit=crop', alt: 'Contract Negotiation' },
      { url: 'https://images.unsplash.com/photo-1758518729263-e26fb50db6bc?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758518729263-e26fb50db6bc?w=400&h=225&fit=crop', alt: 'Tablet Business Meeting' },
      { url: 'https://images.pexels.com/photos/7433919/pexels-photo-7433919.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/7433919/pexels-photo-7433919.jpeg?w=400&h=225&fit=crop', alt: 'Team Planning Session' },
      { url: 'https://images.pexels.com/photos/5439481/pexels-photo-5439481.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/5439481/pexels-photo-5439481.jpeg?w=400&h=225&fit=crop', alt: 'Diverse Business Team' },
      { url: 'https://images.unsplash.com/photo-1758876021212-a87517fc8954?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758876021212-a87517fc8954?w=400&h=225&fit=crop', alt: 'Entrepreneur at Desk' },
      { url: 'https://images.unsplash.com/photo-1758873268174-1f1d6a919a2c?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758873268174-1f1d6a919a2c?w=400&h=225&fit=crop', alt: 'Startup Office Woman' },
      { url: 'https://images.unsplash.com/photo-1758873272353-2ed038aa4ff9?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758873272353-2ed038aa4ff9?w=400&h=225&fit=crop', alt: 'Business Success Celebration' },
      { url: 'https://images.unsplash.com/photo-1758876203468-39e5493bfe76?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1758876203468-39e5493bfe76?w=400&h=225&fit=crop', alt: 'Professional Thinking' },
      { url: 'https://images.pexels.com/photos/6957218/pexels-photo-6957218.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/6957218/pexels-photo-6957218.jpeg?w=400&h=225&fit=crop', alt: 'Startup Team Working' },
      { url: 'https://images.pexels.com/photos/7413872/pexels-photo-7413872.jpeg?w=1920&h=1080&fit=crop', thumbnail: 'https://images.pexels.com/photos/7413872/pexels-photo-7413872.jpeg?w=400&h=225&fit=crop', alt: 'Modern Office Space' },
    ]
  },
  'backgrounds': {
    label: 'Backgrounds & Patterns',
    icon: '🎨',
    images: [
      // Solid colors with subtle patterns - great for text overlays
      { url: 'https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=400&h=225&fit=crop', alt: 'Purple Gradient' },
      { url: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=400&h=225&fit=crop', alt: 'Blue Gradient' },
      { url: 'https://images.unsplash.com/photo-1557683311-eac922347aa1?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1557683311-eac922347aa1?w=400&h=225&fit=crop', alt: 'Yellow Gradient' },
      { url: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=400&h=225&fit=crop', alt: 'Pink Marble' },
      { url: 'https://images.unsplash.com/photo-1550684376-efcbd6e3f031?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1550684376-efcbd6e3f031?w=400&h=225&fit=crop', alt: 'Red Gradient' },
      { url: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=400&h=225&fit=crop', alt: 'Colorful Gradient' },
      // Dark backgrounds
      { url: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=400&h=225&fit=crop', alt: 'Dark Blue Gradient' },
      { url: 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=400&h=225&fit=crop', alt: 'Dark Grid Pattern' },
      { url: 'https://images.unsplash.com/photo-1528459801416-a9e53bbf4e17?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1528459801416-a9e53bbf4e17?w=400&h=225&fit=crop', alt: 'Dark Tech Lines' },
      // Geometric patterns
      { url: 'https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=400&h=225&fit=crop', alt: 'Pink Waves' },
      { url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&h=225&fit=crop', alt: 'Abstract Circles' },
      { url: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=400&h=225&fit=crop', alt: '3D Waves Blue' },
      // Minimal/clean backgrounds
      { url: 'https://images.unsplash.com/photo-1553356084-58ef4a67b2a7?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1553356084-58ef4a67b2a7?w=400&h=225&fit=crop', alt: 'Blue Paint Strokes' },
      { url: 'https://images.unsplash.com/photo-1487147264018-f937fba0c817?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1487147264018-f937fba0c817?w=400&h=225&fit=crop', alt: 'Colorful Smoke' },
      { url: 'https://images.unsplash.com/photo-1558470598-a5dda9640f68?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1558470598-a5dda9640f68?w=400&h=225&fit=crop', alt: 'Green Leaf Minimal' },
      // Textured backgrounds
      { url: 'https://images.unsplash.com/photo-1560015534-cee980ba7e13?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1560015534-cee980ba7e13?w=400&h=225&fit=crop', alt: 'Teal Wall Texture' },
      { url: 'https://images.unsplash.com/photo-1604147706283-d7119b5b822c?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1604147706283-d7119b5b822c?w=400&h=225&fit=crop', alt: 'White Paper Texture' },
      { url: 'https://images.unsplash.com/photo-1533628635777-112b2239b1c7?w=1920&h=1080&fit=crop', thumbnail: 'https://images.unsplash.com/photo-1533628635777-112b2239b1c7?w=400&h=225&fit=crop', alt: 'Grey Concrete' },
    ]
  }
};

const CATEGORY_OPTIONS = [
  { value: 'ai-courses', label: '🤖 AI & Technology' },
  { value: 'retail', label: '🛒 Retail & E-commerce' },
  { value: 'financial', label: '💰 Financial Services' },
  { value: 'life-sciences', label: '🧬 Life Sciences & Healthcare' },
  { value: 'machine-learning', label: '🧠 Machine Learning & Data' },
  { value: 'business', label: '📊 Business & Strategy' },
  { value: 'backgrounds', label: '🎨 Backgrounds & Patterns' },
  { value: 'custom', label: '📁 Custom' },
];

/**
 * ImageLibraryPicker - A dialog component for selecting images from a curated library
 * TenantAdmins can also add custom images to their organization's library
 * SuperAdmins can add images to any organization's library
 */
const ImageLibraryPicker = ({
  open,
  onOpenChange,
  onSelectImage,
  defaultCategory = 'ai-courses'
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);
  
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customImages, setCustomImages] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  
  // Add image dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addCategory, setAddCategory] = useState('custom');
  const [addAlt, setAddAlt] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [targetOrgId, setTargetOrgId] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Crop dialog state
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', x: 0, y: 0, width: 100, height: 56.25, aspect: 16 / 9 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [cropDescription, setCropDescription] = useState('');
  
  // Management mode
  const [manageMode, setManageMode] = useState(false);
  
  const canManageLibrary = user?.role === 'TenantAdmin' || user?.role === 'SuperAdmin';
  const isSuperAdmin = user?.role === 'SuperAdmin';

  // Fetch organizations for SuperAdmin
  useEffect(() => {
    if (open && isSuperAdmin) {
      fetchOrganizations();
    }
  }, [open, isSuperAdmin]);

  // Fetch custom images on open
  useEffect(() => {
    if (open && user?.orgId) {
      fetchCustomImages();
      setTargetOrgId(user.orgId);
    }
  }, [open, user?.orgId]);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/superadmin/organizations');
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchCustomImages = async () => {
    if (!user?.orgId) return;
    setLoadingCustom(true);
    try {
      const response = await api.get(`/organizations/${user.orgId}/image-library`);
      setCustomImages(response.data?.images || []);
    } catch (error) {
      console.error('Error fetching custom images:', error);
    } finally {
      setLoadingCustom(false);
    }
  };

  // Merge default and custom images
  const getMergedLibrary = () => {
    const merged = { ...DEFAULT_IMAGE_LIBRARY };
    
    // Add custom category if not exists
    if (!merged['custom']) {
      merged['custom'] = { label: 'Custom', icon: '📁', images: [] };
    }
    
    // Add custom images to their respective categories
    customImages.forEach(img => {
      const category = img.category || 'custom';
      if (!merged[category]) {
        merged[category] = { label: category, icon: '📁', images: [] };
      }
      merged[category].images.push({
        id: img.id,
        url: img.imageUrl,
        thumbnail: img.thumbnailUrl || img.imageUrl,
        alt: img.alt,
        isCustom: true
      });
    });
    
    return merged;
  };

  const handleSelect = async () => {
    if (!selectedImage) return;
    
    setLoading(true);
    try {
      await onSelectImage(selectedImage.url);
      onOpenChange(false);
      setSelectedImage(null);
    } catch (error) {
      console.error('Error selecting image:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection for upload
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Error', { description: 'Please select an image file' });
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Error', { description: 'Image must be less than 10MB' });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setSelectedFile(file);
      // Pre-fill description from the add dialog if available
      setCropDescription(addAlt || '');
      setShowCropDialog(true);
      setCrop({ unit: '%', x: 0, y: 0, width: 100, height: 56.25, aspect: 16 / 9 });
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle image load for cropping - properly initialize crop
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const aspect = 16 / 9;
    let cropWidth = width;
    let cropHeight = width / aspect;
    
    if (cropHeight > height) {
      cropHeight = height;
      cropWidth = height * aspect;
    }
    
    const x = (width - cropWidth) / 2;
    const y = (height - cropHeight) / 2;
    
    const newCrop = { unit: 'px', x, y, width: cropWidth, height: cropHeight };
    setCompletedCrop(newCrop);
    setCrop({ ...newCrop, aspect: 16 / 9 });
  }, []);

  // Generate cropped image blob
  const getCroppedImg = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!imgRef.current) {
        reject(new Error('Image not loaded'));
        return;
      }
      
      if (!completedCrop || completedCrop.width <= 0 || completedCrop.height <= 0) {
        reject(new Error('Invalid crop area'));
        return;
      }

      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      
      const cropWidth = completedCrop.width * scaleX;
      const cropHeight = completedCrop.height * scaleY;
      
      let outputWidth = Math.min(Math.round(cropWidth), 1920);
      let outputHeight = Math.round(outputWidth / (16/9));
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        cropWidth,
        cropHeight,
        0, 0, outputWidth, outputHeight
      );

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create image blob'));
        }
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop]);

  // Upload cropped image to library
  const handleUploadToLibrary = async () => {
    const description = cropDescription.trim();
    
    if (!description) {
      toast.error('Description Required', { description: 'Please enter a description for the image' });
      return;
    }
    
    if (!completedCrop || completedCrop.width <= 0) {
      toast.error('Crop Required', { description: 'Please adjust the crop area' });
      return;
    }
    
    const uploadOrgId = isSuperAdmin && targetOrgId ? targetOrgId : user.orgId;
    
    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg();
      
      const formData = new FormData();
      formData.append('file', croppedBlob, selectedFile?.name || 'library-image.jpg');
      formData.append('category', addCategory);
      formData.append('alt', description);
      
      await api.post(`/organizations/${uploadOrgId}/image-library/upload`, formData);
      
      toast.success('Success', { description: 'Image added to library!' });
      setShowCropDialog(false);
      setShowAddDialog(false);
      setImageSrc(null);
      setSelectedFile(null);
      setAddAlt('');
      setCropDescription('');
      setAddUrl('');
      fetchCustomImages();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error', { description: error.message || 'Failed to upload image' });
    } finally {
      setUploading(false);
    }
  };

  // Add image from URL
  const handleAddFromUrl = async () => {
    if (!addUrl.trim() || !addAlt.trim()) {
      toast.error('Error', { description: 'Please provide both URL and description' });
      return;
    }
    
    const uploadOrgId = isSuperAdmin && targetOrgId ? targetOrgId : user.orgId;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image_url', addUrl);
      formData.append('category', addCategory);
      formData.append('alt', addAlt);
      
      await api.post(`/organizations/${uploadOrgId}/image-library`, formData);
      
      toast.success('Success', { description: 'Image added to library!' });
      setShowAddDialog(false);
      setAddAlt('');
      setAddUrl('');
      fetchCustomImages();
    } catch (error) {
      console.error('Add error:', error);
      toast.error('Error', { description: error.message || 'Failed to add image' });
    } finally {
      setUploading(false);
    }
  };

  // Delete custom image
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image from the library?')) return;
    
    try {
      await api.delete(`/organizations/${user.orgId}/image-library/${imageId}`);
      toast.success('Success', { description: 'Image deleted from library' });
      fetchCustomImages();
    } catch (error) {
      toast.error('Error', { description: 'Failed to delete image' });
    }
  };

  const mergedLibrary = getMergedLibrary();

  // Check if button should be enabled
  const canAddToLibrary = completedCrop && completedCrop.width > 0 && cropDescription.trim().length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                {manageMode ? 'Manage Image Library' : 'Choose from Image Library'}
              </DialogTitle>
              {canManageLibrary && (
                <div className="flex gap-2">
                  <Button
                    variant={manageMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => setManageMode(!manageMode)}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    {manageMode ? 'Done' : 'Manage'}
                  </Button>
                  {manageMode && (
                    <Button
                      size="sm"
                      onClick={() => setShowAddDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Image
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>
          
          <Tabs 
            value={selectedCategory} 
            onValueChange={(v) => {
              setSelectedCategory(v);
              setSelectedImage(null);
            }}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="grid grid-cols-3 lg:grid-cols-7 gap-1 h-auto p-1">
              {Object.entries(mergedLibrary).map(([key, category]) => (
                category.images.length > 0 || key === 'custom' ? (
                  <TabsTrigger 
                    key={key} 
                    value={key}
                    className="text-xs py-2 px-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    <span className="mr-1">{category.icon}</span>
                    <span className="hidden sm:inline truncate">{category.label}</span>
                  </TabsTrigger>
                ) : null
              ))}
            </TabsList>
            
            {Object.entries(mergedLibrary).map(([key, category]) => (
              <TabsContent 
                key={key} 
                value={key} 
                className="flex-1 overflow-auto mt-4"
              >
                {loadingCustom ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : category.images.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                    <ImageIcon className="w-10 h-10 mb-2" />
                    <p>No images in this category</p>
                    {canManageLibrary && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => {
                          setAddCategory(key);
                          setShowAddDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Image
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-1">
                    {category.images.map((image, idx) => (
                      <div
                        key={image.id || idx}
                        className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all hover:shadow-lg group ${
                          selectedImage?.url === image.url 
                            ? 'border-primary ring-2 ring-primary ring-offset-2' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        onClick={() => !manageMode && setSelectedImage(image)}
                      >
                        <img
                          src={image.thumbnail}
                          alt={image.alt}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {selectedImage?.url === image.url && !manageMode && (
                          <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                        {/* Delete button in manage mode */}
                        {manageMode && image.isCustom && (
                          <button
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image.id);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                        {/* Custom badge */}
                        {image.isCustom && (
                          <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                            Custom
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="text-white text-xs truncate">{image.alt}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
          
          {!manageMode && (
            <div className="flex justify-between items-center pt-4 border-t">
              <p className="text-sm text-gray-500">
                {selectedImage ? `Selected: ${selectedImage.alt}` : 'Click an image to select'}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSelect} 
                  disabled={!selectedImage || loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    'Use This Image'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Image Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Image to Library</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Organization selector for SuperAdmin */}
            {isSuperAdmin && organizations.length > 0 && (
              <div>
                <Label className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  Target Organization
                </Label>
                <Select value={targetOrgId} onValueChange={setTargetOrgId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  As SuperAdmin, you can add images to any organization's library
                </p>
              </div>
            )}
            
            <div>
              <Label>Category</Label>
              <Select value={addCategory} onValueChange={setAddCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description <span className="text-red-500">*</span></Label>
              <Input
                placeholder="E.g., Modern office workspace"
                value={addAlt}
                onChange={(e) => setAddAlt(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Image Source</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="flex-1 border-t" />
                <span>or</span>
                <div className="flex-1 border-t" />
              </div>
              
              <Input
                placeholder="Paste image URL (e.g., from Unsplash)"
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
              />
              {addUrl && (
                <Button
                  className="w-full"
                  onClick={handleAddFromUrl}
                  disabled={uploading || !addAlt.trim()}
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Add from URL
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCropDialog(false);
          setImageSrc(null);
          setSelectedFile(null);
          setCropDescription('');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crop Image (16:9)</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Description field in crop dialog */}
            <div>
              <Label>Description <span className="text-red-500">*</span></Label>
              <Input
                placeholder="E.g., Modern office workspace"
                value={cropDescription}
                onChange={(e) => setCropDescription(e.target.value)}
                className={!cropDescription.trim() ? 'border-orange-300' : ''}
              />
              {!cropDescription.trim() && (
                <p className="text-xs text-orange-500 mt-1">
                  Please enter a description for the image
                </p>
              )}
            </div>
            
            {imageSrc && (
              <div className="flex justify-center bg-gray-100 rounded-lg p-4 max-h-[350px] overflow-auto">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={16 / 9}
                  className="max-w-full"
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    style={{ maxHeight: '300px', maxWidth: '100%' }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
            )}
            
            <p className="text-xs text-gray-400">
              Drag the corners to resize, or drag the crop area to reposition.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCropDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadToLibrary}
              disabled={uploading || !canAddToLibrary}
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add to Library
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageLibraryPicker;
export { DEFAULT_IMAGE_LIBRARY as IMAGE_LIBRARY };
